import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { InputGroup, InputGroupText, Spinner, Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import VirtualKeyboard from "./components/VirtualKeyboard";
import MemberList from "./components/MemberList";
import neoLogo from "../../assets/images/Neo_Logo_A.svg";
// BubbleInactivity removido — muito pesado para totems (animações contínuas + imagens)
import KioskGamificationProfile from "./components/KioskGamificationProfile";

import { useKiosk } from "./hooks/useKiosk";
import TVDisplay from "./TVDisplay";
import MemoryGame from "./components/MemoryGame";

// Backgrounds
import bgSwimming from "../../assets/images/new1Bg.png";
import bgRunning from "../../assets/images/kiosk_bgs/bg_running_1777982795262.png";
import bgWeightlifting from "../../assets/images/kiosk_bgs/bg_weightlifting_1777982840440.png";
import bgCrossfit from "../../assets/images/kiosk_bgs/bg_crossfit_1777982887027.png";

const BACKGROUNDS = {
    'swimming': bgSwimming,
    'running': bgRunning,
    'weightlifting': bgWeightlifting,
    'crossfit': bgCrossfit
};

const KioskPage = () => {
    document.title = "Autoatendimento | NeoApp";
    const navigate = useNavigate();

    const {
        tenantLogo,
        tenantLoading,
        loadingMembers,
        searchTerm,
        isKeyboardVisible,
        selectedMember,
        filteredMembers,
        debouncedSearchTerm,
        actions: {
            handleKeyPress,
            handleDelete,
            handleSpace,
            handleClear,
            handleSelectMember,
            handleBack,
            hideKeyboard,
            toggleKeyboard,
            showKeyboard,
            setSearchTerm
        },
        kioskBackground
    } = useKiosk();

    // Determine current background
    const currentBg = (kioskBackground && BACKGROUNDS[kioskBackground]) ? BACKGROUNDS[kioskBackground] : BACKGROUNDS['swimming'];

    // Preload da imagem de fundo para evitar piscar quando a página carregar
    const [bgLoaded, setBgLoaded] = useState(false);
    useEffect(() => {
        const img = new Image();
        img.src = currentBg;
        img.onload = () => setBgLoaded(true);
        img.onerror = () => setBgLoaded(true); // Se falhar, segue em frente
    }, [currentBg]);

    // ═══ Lógica de Inatividade (Screensaver) ═══
    const [isIdle, setIsIdle] = useState(false);
    const [showMemoryGame, setShowMemoryGame] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordInput, setPasswordInput] = useState("");

    const handleProfessorClick = () => {
        setShowPasswordModal(true);
        setPasswordInput("");
    };

    const handleNumPadClick = (num) => {
        if (passwordInput.length < 5) {
            const newPassword = passwordInput + num;
            setPasswordInput(newPassword);
            if (newPassword.length === 5) {
                if (newPassword === "00000") {
                    setTimeout(() => {
                        setShowPasswordModal(false);
                        navigate("/kiosk/professors");
                    }, 200);
                } else {
                    // wrong password
                    setTimeout(() => {
                        setPasswordInput("");
                    }, 400);
                }
            }
        }
    };

    const handleNumPadDelete = () => {
        setPasswordInput(prev => prev.slice(0, -1));
    };

    useEffect(() => {
        let idleTimer;

        const resetTimer = () => {
            setIsIdle(false);
            clearTimeout(idleTimer);
            // Inicia o timer novamente
            idleTimer = setTimeout(() => {
                setIsIdle(true);
                handleClear(); // Limpa busca do usuário caso tenha deixado algo
                handleBack();  // Fecha modal de perfil caso o aluno tenha deixado aberto
            }, 60000); // 1 minuto
        };

        resetTimer();

        const handleActivity = () => {
            resetTimer();
        };

        const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
        events.forEach(e => window.addEventListener(e, handleActivity));

        return () => {
            clearTimeout(idleTimer);
            events.forEach(e => window.removeEventListener(e, handleActivity));
        };
    }, [handleClear, handleBack]);

    // Modelo Padrão de Loading Otimizado (igual a outras páginas, aguardando tudo inclusive a Imagem BG)
    if (tenantLoading || loadingMembers || !bgLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", backgroundColor: "#f8f8fb" }}>
                <div className="text-center">
                    <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                    <p className="text-muted mt-3 fw-medium font-size-16">Carregando sistema...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="d-flex flex-column"
            style={{
                minHeight: "100vh",
                position: "relative",
                backgroundColor: "#001D3D", // Fallback color to avoid white flash
                backgroundImage: `url(${currentBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            <style>
                {`
                /* KIOSK HARDWARE ACCELERATION FIX */
                /* Totems and TVs lack GPUs. backdrop-filters cause massive lag rendering 2-5fps */
                /* This rule catches any child component or library that sets backdrop-filter via CSS class */
                * {
                    backdrop-filter: none !important;
                    -webkit-backdrop-filter: none !important;
                }
                /* Disable heavy animations for low-powered devices */
                .soap-bubble {
                    backdrop-filter: none !important;
                    -webkit-backdrop-filter: none !important;
                }
                `}
            </style>
            {/* ═══ Screensaver Overlay (Z-index muito alto) ═══ */}
            {isIdle && (
                <div
                    style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 100000 }}
                    onClick={() => setIsIdle(false)} // Qualquer clique acorda
                >
                    {/* Renderizamos o componente TVDisplay que tem o planejamento e aulas do dia */}
                    <TVDisplay />

                    {/* Overlay de captura de clique e botão pulsante */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: "transparent",
                            zIndex: 100001,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center",
                            paddingBottom: "2vh"
                        }}
                    >
                        <style>
                            {`
                        @keyframes pulseGlowWhite {
                            0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6); }
                            50% { transform: scale(1.08); box-shadow: 0 0 0 25px rgba(255, 255, 255, 0); }
                            100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
                        }
                        .kiosk-pulse-btn {
                            animation: pulseGlowWhite 2.5s infinite;
                            background-color: rgba(255, 255, 255, 0.85);
                            width: 60px;
                            height: 60px;
                            border: 1px solid rgba(255, 255, 255, 0.5);
                        }
                        `}
                        </style>

                        <div className="kiosk-pulse-btn rounded-circle shadow-lg d-flex align-items-center justify-content-center">
                            <i className="mdi mdi-gesture-tap" style={{ fontSize: "1.8rem", color: "#6c757d", marginTop: "-2px" }}></i>
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay claro e desfocado */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(240, 242, 246, 0.92)", // Fundo opaco leve (sem blur para performance em totems)
                    zIndex: 0,
                    willChange: "transform",
                    transform: "translateZ(0)" // Force GPU layer
                }}
            ></div>

            {/* Conteúdo Principal (z-index: 1 para ficar acima do overlay) */}
            <div className="d-flex flex-column" style={{ zIndex: 1, position: "relative", minHeight: "100vh" }}>


                {selectedMember ? (
                    <KioskGamificationProfile
                        member={selectedMember}
                        onBack={handleBack}
                        tenantLogo={tenantLogo}
                    />
                ) : (
                    <>
                        {/* ═══ Header (Topo) ═══ */}
                        <div className="px-4 py-3 position-relative flex-shrink-0">

                            {/* Botões Superiores Direitos */}
                            <div className="position-absolute top-0 end-0 p-4 mt-1 me-1 d-flex gap-2" style={{ zIndex: 100 }}>
                                <button
                                    onClick={() => setShowMemoryGame(true)}
                                    className="btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0 border"
                                    style={{ width: "45px", height: "45px" }}
                                    title="Jogo da Memória"
                                >
                                    <i className="mdi mdi-cards-playing-outline font-size-22 text-secondary"></i>
                                </button>
                                <button
                                    onClick={handleProfessorClick}
                                    className="btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0 border"
                                    style={{ width: "45px", height: "45px" }}
                                    title="Ranking de Professores"
                                >
                                    <i className="mdi mdi-account-tie-outline font-size-22 text-secondary"></i>
                                </button>
                                <button
                                    onClick={() => navigate("/kiosk/evaluation")}
                                    className="btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0 border"
                                    style={{ width: "45px", height: "45px" }}
                                    title="Matriz de Avaliações"
                                >
                                    <i className="mdi mdi-information-outline font-size-22 text-secondary"></i>
                                </button>
                            </div>

                            {/* Logo da Empresa no canto superior esquerdo */}
                            <div className="d-flex justify-content-start w-100 mb-2">
                                {tenantLogo && (
                                    <img
                                        src={tenantLogo}
                                        alt="Logo Academia"
                                        style={{ maxHeight: "45px", maxWidth: "160px", objectFit: "contain" }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* ═══ Conteúdo Central — Busca em cima, Teclado fixo embaixo ═══ */}
                        <div
                            className="flex-grow-1 d-flex flex-column justify-content-start align-items-center px-3 py-3"
                            onClick={hideKeyboard}
                            style={{ overflow: "hidden", paddingTop: "5vh" }}
                        >
                            {/* Container Unificado (Busca + Resultados) — fica no topo */}
                            <div
                                className="d-flex flex-column w-100 shadow-lg"
                                style={{
                                    maxWidth: "680px",
                                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                                    borderRadius: "16px",
                                    border: "2px solid #482712",
                                    overflow: "hidden",
                                    transition: "all 0.3s ease-in-out",
                                    boxShadow: "var(--shadow-premium-elevated)"
                                }}
                            >
                                {/* Barra de Busca Interna (Sticky Top) */}
                                <div
                                    className="px-4 py-2"
                                    style={{
                                        borderBottom: "none",
                                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                                        transition: "border-color 0.3s ease"
                                    }}
                                >
                                    <InputGroup className="border-0 bg-transparent flex-nowrap align-items-center">
                                        <InputGroupText className="bg-transparent border-0 ps-1 pe-3 py-0">
                                            <i className="mdi mdi-magnify font-size-28" style={{ color: "#482712" }}></i>
                                        </InputGroupText>
                                        <input
                                            type="text"
                                            placeholder="Digite seu nome..."
                                            value={searchTerm}
                                            className="border-0 bg-transparent fw-bold py-3 px-0 w-100"
                                            style={{
                                                fontSize: "32px",
                                                color: "#482712",
                                                fontFamily: "'Outfit', sans-serif",
                                                border: "none",
                                                outline: "none",
                                                boxShadow: "none",
                                                backgroundColor: "transparent"
                                            }}
                                            inputMode="none"
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onClick={(e) => { e.stopPropagation(); showKeyboard(); }}
                                        />
                                        {searchTerm && (
                                            <InputGroupText
                                                className="bg-transparent border-0 pe-1 py-0"
                                                style={{ cursor: "pointer", zIndex: 10 }}
                                                onClick={(e) => { e.stopPropagation(); handleClear(); }}
                                            >
                                                <i className="mdi mdi-close-circle text-muted font-size-24 hover-opacity-100 transition-all opacity-50"></i>
                                            </InputGroupText>
                                        )}
                                    </InputGroup>
                                </div>

                                {/* Listagem Interna (Área de scroll) - Condicionada a ter buscas */}
                                {((debouncedSearchTerm && debouncedSearchTerm.trim().length >= 2) || loadingMembers) && (
                                    <div className="overflow-auto custom-scrollbar position-relative" style={{ maxHeight: "35vh" }}>
                                        <MemberList
                                            members={filteredMembers}
                                            loading={loadingMembers}
                                            onSelect={handleSelectMember}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ═══ Teclado Virtual (ancorado logo abaixo, empurrado pela lista) ═══ */}
                            {isKeyboardVisible && (
                                <div className="w-100" style={{ transition: "margin-top 0.3s", marginTop: "8vh" }} onClick={(e) => e.stopPropagation()}>
                                    <VirtualKeyboard
                                        onKeyPress={handleKeyPress}
                                        onDelete={handleDelete}
                                        onSpace={handleSpace}
                                        onClear={handleClear}
                                    />
                                </div>
                            )}
                        </div>

                        {/* ═══ Footer Fino (Powered By + Toggle) ═══ */}
                        <div className="flex-shrink-0 position-relative" style={{ zIndex: 10 }}>

                            {/* Botão Flutuante para minimizar/expandir teclado */}
                            <div
                                className="position-absolute start-50 translate-middle-x"
                                style={{ top: "-18px", zIndex: 12 }}
                            >
                                <button
                                    className="btn btn-light shadow-sm border rounded-pill d-flex align-items-center justify-content-center"
                                    style={{ width: "48px", height: "36px", backgroundColor: "rgba(255, 255, 255, 0.95)" }}
                                    onClick={toggleKeyboard}
                                >
                                    <i className={`mdi ${isKeyboardVisible ? 'mdi-chevron-down' : 'mdi-chevron-up'} font-size-24 text-muted`} style={{ marginTop: "-2px" }}></i>
                                </button>
                            </div>

                            {/* Logo da Empresa Powered By */}
                            <div
                                className="d-flex justify-content-end align-items-center py-3 px-4"
                                onClick={() => navigate("/dashboard")}
                                style={{ cursor: "pointer" }}
                                title="Ir para o Dashboard Interno"
                            >
                                <span style={{ fontSize: "11px", fontWeight: "800", color: "#b3b3b3", letterSpacing: "1px" }} className="me-2 text-uppercase">POWERED BY</span>
                                <img src={neoLogo} alt="NeoApp" style={{ height: "14px", filter: "grayscale(100%) opacity(0.5)" }} />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {showMemoryGame && <MemoryGame onClose={() => setShowMemoryGame(false)} />}

            {/* Modal de Senha para Ranking de Professores */}
            <Modal isOpen={showPasswordModal} toggle={() => setShowPasswordModal(!showPasswordModal)} centered>
                <ModalHeader toggle={() => setShowPasswordModal(!showPasswordModal)} className="border-0 pb-0">
                    Acesso Restrito
                </ModalHeader>
                <ModalBody className="text-center pt-0 pb-4">
                    <p className="text-muted mb-4">Digite a senha para acessar o desempenho</p>

                    <div className="d-flex justify-content-center gap-2 mb-4">
                        {[0, 1, 2, 3, 4].map(i => (
                            <div key={i} className="rounded-circle border d-flex align-items-center justify-content-center"
                                style={{ width: "20px", height: "20px", backgroundColor: i < passwordInput.length ? "#343a40" : "transparent", transition: "background-color 0.2s" }}>
                            </div>
                        ))}
                    </div>

                    <div className="d-flex flex-column align-items-center">
                        <div className="d-flex justify-content-center gap-2 mb-2">
                            {[1, 2, 3].map(num => (
                                <Button key={num} color="light" className="rounded-circle shadow-sm fw-bold border" style={{ width: "60px", height: "60px", fontSize: "24px" }} onClick={() => handleNumPadClick(num.toString())}>{num}</Button>
                            ))}
                        </div>
                        <div className="d-flex justify-content-center gap-2 mb-2">
                            {[4, 5, 6].map(num => (
                                <Button key={num} color="light" className="rounded-circle shadow-sm fw-bold border" style={{ width: "60px", height: "60px", fontSize: "24px" }} onClick={() => handleNumPadClick(num.toString())}>{num}</Button>
                            ))}
                        </div>
                        <div className="d-flex justify-content-center gap-2 mb-2">
                            {[7, 8, 9].map(num => (
                                <Button key={num} color="light" className="rounded-circle shadow-sm fw-bold border" style={{ width: "60px", height: "60px", fontSize: "24px" }} onClick={() => handleNumPadClick(num.toString())}>{num}</Button>
                            ))}
                        </div>
                        <div className="d-flex justify-content-center gap-2">
                            <div style={{ width: "60px", height: "60px" }}></div>
                            <Button color="light" className="rounded-circle shadow-sm fw-bold border" style={{ width: "60px", height: "60px", fontSize: "24px" }} onClick={() => handleNumPadClick("0")}>0</Button>
                            <Button color="light" className="rounded-circle shadow-sm text-danger border" style={{ width: "60px", height: "60px", fontSize: "24px" }} onClick={handleNumPadDelete}><i className="mdi mdi-backspace-outline"></i></Button>
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default KioskPage;

